import { Toggle } from 'framework7-react';
import { FC, ReactNode, useRef, useState } from 'react';

import { EventMetaFilter, PublicFeedEvent } from '@shared/types/event';
import { uniq } from 'lodash';

import { useCurrentUserEventCritiqueQuery } from '@queries/EventQueries';

import { useCritiqueDocumentMutation } from '@mutations/CritiqueMutations';

import { useIsVisible } from '@hooks/useIsVisible';

import { useNavigate } from '@router/index';

import IconButton from '@components/buttons/IconButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { LineAwesomeIcon } from '@components/icons/LineAwesomeIcon';

import { useEventRsvpActions } from '../../event/EventRsvpScreen/hooks/useEventRsvpActions';
import {
  SuperAdminEventCategoryToggles,
  SuperAdminEventCategoryTogglesUnwrapped,
} from './SuperAdminEventCategoryToggles';

const IMAGE_SIZE = 60;

const EventRsvpCardDetails: FC<{ event: PublicFeedEvent; action?: ReactNode }> = ({
  event,
  action,
}) => {
  return (
    <div className="py-2">
      <div
        className="d-flex gap-1 justify-content-between align-items-center mb-1"
        style={{ lineHeight: 1 }}
      >
        <div>
          <strong>{event.title}</strong>
        </div>
        {action && <div className="ms-auto">{action}</div>}
      </div>
      <div style={{ fontSize: '.7rem', opacity: 0.8 }}>
        <div>
          <span className="me-1">
            <LineAwesomeIcon name="clock" />
          </span>
          {event.start}
        </div>
        <div>
          <span className="me-1">
            <LineAwesomeIcon name="map-marker" />
          </span>
          {event.venue.title}
        </div>
      </div>
    </div>
  );
};

const EntertainerRsvpCardActions: FC<{ event: PublicFeedEvent; onInviteClick: () => void }> = ({
  event,
  onInviteClick,
}) => {
  const { rsvp, handleMarkAttending, handleMarkInterested } = useEventRsvpActions({
    eventId: event._id,
    eventTitle: event.title,
    actionToken: event.actionToken,
  });

  const isCurrentUserAttending = rsvp?.rsvp === 'yes';
  const isCurrentUserInterested = rsvp?.rsvp !== 'yes' && rsvp?.eventInterest === 'interested';

  const attendingCount = event.attending ?? 0;
  const interestedCount = event.interested ?? 0;

  return (
    <div className="border-0 pt-0" style={{ fontSize: 12 }}>
      <div className="d-flex justify-content-between">
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            handleMarkAttending();
          }}
          className="d-flex align-items-center gap-2"
        >
          <div style={{ fontSize: 14 }}>
            <FontAwesomeIcon
              faStyle={isCurrentUserAttending ? 'fas' : 'far'}
              name={isCurrentUserAttending ? 'calendar-check' : 'calendar-plus'}
              style={{ color: isCurrentUserAttending ? 'var(--color-green)' : undefined }}
            />
          </div>
          <div>
            <span>{attendingCount} Attending</span>
          </div>
        </div>
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            handleMarkInterested();
          }}
          className="d-flex align-items-center gap-2"
        >
          <div style={{ fontSize: 14 }}>
            <FontAwesomeIcon
              faStyle={isCurrentUserInterested ? 'fas' : 'far'}
              name="star"
              style={{ color: isCurrentUserInterested ? 'var(--color-yellow)' : undefined }}
            />
          </div>
          <div>
            <span>{interestedCount} Interested</span>
          </div>
        </div>
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            onInviteClick();
          }}
          className="d-flex align-items-center gap-2"
        >
          <div style={{ fontSize: 14 }}>
            <FontAwesomeIcon faStyle="far" name="paper-plane" />
          </div>
          <div>
            <span>Invite</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const EventRsvpCard: FC<{
  event: PublicFeedEvent;
  variant: 'small' | 'large';
  onInviteClick: () => void;
  isSuperAdminMode: boolean;
  eventMetaFilters?: EventMetaFilter[];
}> = ({ event, variant, onInviteClick, isSuperAdminMode, eventMetaFilters }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const { isVisible } = useIsVisible(cardRef);
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/events/${event._id}/rsvp`);
  };

  if (variant === 'large') {
    const rsvpText = [`${event.attending} attending`, `${event.interested} interested`].join(' · ');

    return (
      <div
        ref={cardRef}
        className="ps-3 pe-3"
        style={{ background: 'rgba(255, 255, 255, 0.2)', borderRadius: 8 }}
      >
        <div className="d-flex align-items-center gap-3" role="button" onClick={handleClick}>
          <div className="d-flex align-items-center pt-3 pb-2">
            <img
              src={isVisible ? event.img : undefined}
              alt={event.title}
              style={{
                width: IMAGE_SIZE * 1.5,
                height: IMAGE_SIZE * 1.5,
                objectFit: 'cover',
                borderRadius: 8,
              }}
            />
          </div>
          <div className="d-flex justify-content-between w-100">
            <div>
              <EventRsvpCardDetails
                event={event}
                action={
                  <IconButton color="neutral" variant="smooth">
                    <FontAwesomeIcon name="chevron-right" />
                  </IconButton>
                }
              />
            </div>
          </div>
        </div>
        <div className="pb-2">
          <EntertainerRsvpCardActions event={event} onInviteClick={onInviteClick} />

          {/* <div style={{ fontSize: '.7rem', opacity: 0.8 }}>{rsvpText}</div>
          <div className="mt-2 d-flex gap-2">
            <Button
              className="py-2 px-3"
              variant="smooth"
              color="neutral"
              size="sm"
              onClick={handleRsvpClick}
            >
              RSVP
            </Button>
            <Button
              className="py-2 px-3"
              variant="outlined"
              color="neutral"
              size="sm"
              startIcon={<FontAwesomeIcon name="paper-plane" />}
              onClick={handleInviteClick}
            >
              Invite
            </Button>
          </div> */}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      className="d-grid ps-3 pe-3"
      style={{ background: 'rgba(255, 255, 255, 0.2)', borderRadius: 8 }}
    >
      <div className="d-flex gap-2 justify-content-between align-items-center w-100">
        <div className="d-flex align-items-center gap-3" role="button" onClick={handleClick}>
          <div className="d-flex align-items-center py-3">
            <img
              src={isVisible ? event.img : undefined}
              alt={event.title}
              style={{ width: IMAGE_SIZE, height: IMAGE_SIZE, objectFit: 'cover', borderRadius: 8 }}
            />
          </div>
          <EventRsvpCardDetails event={event} />
        </div>
        <div>
          <IconButton color="neutral" variant="smooth" onClick={handleClick}>
            <FontAwesomeIcon name="chevron-right" />
          </IconButton>
        </div>
        {/* <div>
          <Button
            className="py-1 px-3"
            variant="outlined"
            color="neutral"
            size="sm"
            onClick={handleRsvpClick}
          >
            RSVP
          </Button>
        </div> */}
      </div>
      <div className="pb-2">
        <EntertainerRsvpCardActions event={event} onInviteClick={onInviteClick} />
      </div>
      {isSuperAdminMode && (
        <SuperAdminEventCategoryTogglesUnwrapped
          event={event}
          eventMetaFilters={eventMetaFilters}
        />
      )}
    </div>
  );
};
